import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const normalizeComboOffer = (item = {}) => ({
  combo_id: String(item?.combo_id ?? item?.id ?? ""),
  combo_name: String(item?.combo_name ?? ""),
  combo_price: String(item?.combo_price ?? ""),
  combo_description: String(item?.combo_description ?? ""),
  combo_type: String(item?.combo_type ?? ""),
  is_configurable: String(item?.is_configurable ?? ""),
  is_active: String(item?.is_active ?? ""),
});

const normalizeOptionGroupItem = (item = {}) => ({
  pk_option_group_item_id: String(item?.pk_option_group_item_id ?? ""),
  fk_combo_id: String(item?.fk_combo_id ?? item?.combo_id ?? ""),
  item_selection_name: String(item?.item_selection_name ?? ""),
  minimum_selection: String(item?.minimum_selection ?? "0"),
  maximum_selection: String(item?.maximum_selection ?? "0"),
  is_item_configurable: String(
    item?.is_item_configurable ?? item?.is_configurable ?? "N",
  ),
  is_configurable: String(
    item?.is_item_configurable ?? item?.is_configurable ?? "N",
  ),
  items: Array.isArray(item?.items) ? item.items : [],
});

export default function useCombo() {
  const { api, userID } = useContext(GlobalContext);

  // Prevent duplicate requests caused by StrictMode,
  // fast double-clicks, or overlapping UI calls.
  const comboInsertPromiseRef = useRef(null);
  const comboFetchPromiseRef = useRef(new Map());
  const optionGroupInsertPromiseRef = useRef(null);
  const optionGroupItemsFetchPromiseRef = useRef(new Map());
  const comboConfigurableInsertPromiseRef = useRef(null);
  const catalogV3FetchPromiseRef = useRef(null);

  // ================= COMBO OFFER =================

  const [comboOffers, setComboOffers] = useState([]);
  const [comboOffersLoading, setComboOffersLoading] = useState(false);
  const [comboOfferSaving, setComboOfferSaving] = useState(false);

  // ================= OPTION GROUP ITEMS =================

  // Stored combo-wise:
  const [optionGroupItems, setOptionGroupItems] = useState({});
  const [optionGroupItemsLoading, setOptionGroupItemsLoading] = useState({});
  const [optionGroupSaving, setOptionGroupSaving] = useState(false);

  // ================= CONFIGURABLE COMBO ITEMS =================
  const [comboConfigurableSaving, setComboConfigurableSaving] = useState(false);

  // ================= CATALOG V3 =================
  const [catalogV3Loading, setCatalogV3Loading] = useState(false);

  // =====================================================
  // INSERT COMBO OFFER
  // =====================================================

  const insertComboOffer = async (payload = {}) => {
    if (comboInsertPromiseRef.current) {
      return comboInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        setComboOfferSaving(true);

        const requestPayload = {
          combo_name: String(payload?.combo_name ?? "").trim(),
          combo_price: String(payload?.combo_price ?? "").trim(),
          combo_description: String(payload?.combo_description ?? "").trim(),
          combo_type: String(payload?.combo_type ?? "").trim(),
          is_configurable: String(payload?.is_configurable ?? "N").trim(),
          is_active: String(payload?.is_active ?? "Y").trim(),
        };

        if (!requestPayload.combo_name) {
          toast.error("Combo name is required");
          return false;
        }

        const res = await api.post("/Order/insertComboOffer", requestPayload);

        if (res?.data?.responseCode === "00") {
          toast.success(
            res?.data?.responseMessage || "Combo offer added successfully",
          );

          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to add combo offer");

        return false;
      } catch (err) {
        console.error("Insert combo offer error:", err?.response?.data || err);

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding combo offer",
        );

        return false;
      } finally {
        setComboOfferSaving(false);
      }
    })();

    comboInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (comboInsertPromiseRef.current === requestPromise) {
        comboInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // GET COMBO OFFER
  // =====================================================

  const getComboOffer = async (comboName = "", options = {}) => {
    const finalComboName = String(comboName ?? "").trim();
    const requestKey = finalComboName.toLowerCase();
    const forceRefresh = options?.force === true;

    if (!(comboFetchPromiseRef.current instanceof Map)) {
      comboFetchPromiseRef.current = new Map();
    }

    const existingPromise = comboFetchPromiseRef.current.get(requestKey);

    if (existingPromise && !forceRefresh) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setComboOffersLoading(true);

        const requestPayload = {
          combo_name: finalComboName,
        };

        const res = await api.post("/Order/getComboOffer", requestPayload, {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        });

        if (res?.data?.responseCode === "00") {
          const responseData =
            res?.data?.response || res?.data?.data || res?.data?.result || [];

          const finalData = Array.isArray(responseData)
            ? responseData.map(normalizeComboOffer)
            : [];

          setComboOffers(finalData);

          return finalData;
        }

        setComboOffers([]);

        toast.error(
          res?.data?.responseMessage || "Failed to fetch combo offers",
        );

        return [];
      } catch (err) {
        console.error("Get combo offer error:", err?.response?.data || err);

        setComboOffers([]);

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching combo offers",
        );

        return [];
      } finally {
        setComboOffersLoading(false);
      }
    })();

    if (!(comboFetchPromiseRef.current instanceof Map)) {
      comboFetchPromiseRef.current = new Map();
    }

    comboFetchPromiseRef.current.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        comboFetchPromiseRef.current instanceof Map &&
        comboFetchPromiseRef.current.get(requestKey) === requestPromise
      ) {
        comboFetchPromiseRef.current.delete(requestKey);
      }
    }
  };

  // =====================================================
  // GET COMBO OFFER - SAME API ALIAS.
  // =====================================================

  const fetchComboOffers = async (comboName = "") => {
    return getComboOffer(comboName);
  };

  // =====================================================
  // INSERT COMBO CONFIGURABLE
  // =====================================================

  const insertComboConfigurable = async (payload = {}) => {
    if (comboConfigurableInsertPromiseRef.current) {
      return comboConfigurableInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        const finalUserId = String(
          payload?.user_id ??
            payload?.userid ??
            payload?.userId ??
            userID ??
            "",
        ).trim();

        const finalComboId = String(
          payload?.combo_id ?? payload?.comboId ?? "",
        ).trim();

        const finalOptionGroupItemId = String(
          payload?.option_group_item_id ??
            payload?.optionGroupItemId ??
            payload?.optionGroupId ??
            "",
        ).trim();

        const rawItemIds =
          payload?.item_id ?? payload?.itemId ?? payload?.itemIds ?? [];

        const finalItemIds = (
          Array.isArray(rawItemIds) ? rawItemIds : [rawItemIds]
        )
          .map((id) => String(id ?? "").trim())
          .filter(Boolean);

        if (!finalUserId) {
          toast.error("User ID is required");
          return false;
        }

        if (!finalComboId) {
          toast.error("Combo ID is required");
          return false;
        }

        if (!finalOptionGroupItemId) {
          toast.error("Option group item ID is required");
          return false;
        }

        if (!finalItemIds.length) {
          toast.error("Please select at least one item");
          return false;
        }

        setComboConfigurableSaving(true);

        const requestPayload = {
          user_id: finalUserId,
          combo_id: finalComboId,
          option_group_item_id: finalOptionGroupItemId,
          item_id: finalItemIds,
        };

        const res = await api.post(
          "/Order/insertComboConfigurable",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          return true;
        }

        toast.error(
          res?.data?.responseMessage ||
            "Failed to insert combo configurable items",
        );

        return false;
      } catch (err) {
        console.error(
          "Insert combo configurable error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error inserting combo configurable items",
        );

        return false;
      } finally {
        setComboConfigurableSaving(false);
      }
    })();

    comboConfigurableInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (comboConfigurableInsertPromiseRef.current === requestPromise) {
        comboConfigurableInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // GET CATALOG ITEMS V3
  // =====================================================

  const getCatalogItemsV3 = async (payload = {}) => {
    if (catalogV3FetchPromiseRef.current) {
      return catalogV3FetchPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return null;
        }

        const requestPayload = {
          userid: String(
            payload?.userid ??
              payload?.user_id ??
              payload?.userId ??
              userID ??
              "",
          ).trim(),
          orderType: String(
            payload?.orderType ?? payload?.order_type ?? "takeaway",
          ).trim(),
          platform: String(payload?.platform ?? "pos").trim(),
        };

        if (!requestPayload.userid) {
          toast.error("User ID is required");
          return null;
        }

        setCatalogV3Loading(true);

        const res = await api.post(
          "/Order/v3.0/getCatalogItems",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          return (
            res?.data?.response ?? res?.data?.data ?? res?.data?.result ?? null
          );
        }

        toast.error(
          res?.data?.responseMessage || "Failed to fetch v3 catalogue",
        );

        return null;
      } catch (err) {
        console.error(
          "Get catalog items v3 error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching v3 catalogue",
        );

        return null;
      } finally {
        setCatalogV3Loading(false);
      }
    })();

    catalogV3FetchPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (catalogV3FetchPromiseRef.current === requestPromise) {
        catalogV3FetchPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // INSERT COMBO OPTION GROUP DETAILS
  // =====================================================

  const insertComboOptionGroupDetails = async (payload = {}) => {
    if (optionGroupInsertPromiseRef.current) {
      return optionGroupInsertPromiseRef.current;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return false;
        }

        const requestPayload = {
          fkComboId: String(
            payload?.fkComboId ?? payload?.fk_combo_id ?? "",
          ).trim(),
          itemSelectionName: String(
            payload?.itemSelectionName ?? payload?.item_selection_name ?? "",
          ).trim(),
          minimumSelection: String(
            payload?.minimumSelection ?? payload?.minimum_selection ?? "0",
          ).trim(),
          maximumSelection: String(
            payload?.maximumSelection ?? payload?.maximum_selection ?? "0",
          ).trim(),
          isConfigurable: String(
            payload?.isConfigurable ?? payload?.is_configurable ?? "N",
          ).trim(),
        };

        if (!requestPayload.fkComboId) {
          toast.error("Combo ID is required");
          return false;
        }

        if (!requestPayload.itemSelectionName) {
          toast.error("Item selection name is required");
          return false;
        }

        const minSelection = Number(requestPayload.minimumSelection);
        const maxSelection = Number(requestPayload.maximumSelection);

        if (
          !Number.isFinite(minSelection) ||
          !Number.isFinite(maxSelection) ||
          minSelection < 0 ||
          maxSelection < 0
        ) {
          toast.error("Minimum and maximum selection must be valid numbers");
          return false;
        }

        if (maxSelection < minSelection) {
          toast.error(
            "Maximum selection cannot be less than minimum selection",
          );
          return false;
        }

        setOptionGroupSaving(true);

        const res = await api.post(
          "/Order/insertComboOptionGroupDetails",
          requestPayload,
        );

        console.log("INSERT COMBO OPTION GROUP DETAILS RESPONSE:", res?.data);

        if (res?.data?.responseCode === "00") {
          // toast.success(
          //   res?.data?.responseMessage || "Option group added successfully",
          // );

          return true;
        }

        toast.error(res?.data?.responseMessage || "Failed to add option group");

        return false;
      } catch (err) {
        console.error(
          "Insert combo option group details error:",
          err?.response?.data || err,
        );

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error adding option group",
        );

        return false;
      } finally {
        setOptionGroupSaving(false);
      }
    })();

    optionGroupInsertPromiseRef.current = requestPromise;

    try {
      return await requestPromise;
    } finally {
      if (optionGroupInsertPromiseRef.current === requestPromise) {
        optionGroupInsertPromiseRef.current = null;
      }
    }
  };

  // =====================================================
  // GET OPTION GROUP ITEMS
  // =====================================================

  const getOptionGroupItems = async (comboId) => {
    const finalComboId = String(comboId ?? "").trim();

    if (!finalComboId) {
      toast.error("Combo ID is required");
      return [];
    }

    const existingPromise =
      optionGroupItemsFetchPromiseRef.current.get(finalComboId);

    if (existingPromise) {
      return existingPromise;
    }

    const requestPromise = (async () => {
      try {
        if (!api) {
          toast.error("Main Anlook API instance missing");
          return [];
        }

        setOptionGroupItemsLoading((prev) => ({
          ...prev,
          [finalComboId]: true,
        }));

        const requestPayload = {
          combo_id: finalComboId,
        };

        const res = await api.post(
          "/Order/getOptionGroupItems",
          requestPayload,
        );

        if (res?.data?.responseCode === "00") {
          const responseData =
            res?.data?.response || res?.data?.data || res?.data?.result || [];

          const normalizedData = Array.isArray(responseData)
            ? responseData.map(normalizeOptionGroupItem)
            : [];

          const groupedByComboId = normalizedData.reduce((acc, item) => {
            const comboId = String(item?.fk_combo_id ?? "").trim();

            if (!comboId) return acc;

            if (!acc[comboId]) {
              acc[comboId] = [];
            }

            acc[comboId].push(item);
            return acc;
          }, {});

          const selectedComboItems = groupedByComboId[finalComboId] || [];

          setOptionGroupItems((prev) => ({
            ...prev,
            ...groupedByComboId,
            [finalComboId]: selectedComboItems,
          }));

          return selectedComboItems;
        }

        setOptionGroupItems((prev) => ({
          ...prev,
          [finalComboId]: [],
        }));

        toast.error(
          res?.data?.responseMessage || "Failed to fetch option group items",
        );

        return [];
      } catch (err) {
        console.error(
          "Get option group items error:",
          err?.response?.data || err,
        );

        setOptionGroupItems((prev) => ({
          ...prev,
          [finalComboId]: [],
        }));

        toast.error(
          err?.response?.data?.responseMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Error fetching option group items",
        );

        return [];
      } finally {
        setOptionGroupItemsLoading((prev) => ({
          ...prev,
          [finalComboId]: false,
        }));
      }
    })();

    optionGroupItemsFetchPromiseRef.current.set(finalComboId, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (
        optionGroupItemsFetchPromiseRef.current.get(finalComboId) ===
        requestPromise
      ) {
        optionGroupItemsFetchPromiseRef.current.delete(finalComboId);
      }
    }
  };

  return {
    // ================= COMBO OFFERS =================
    comboOffers,
    comboOffersLoading,
    comboOfferSaving,

    insertComboOffer,
    getComboOffer,
    fetchComboOffers,

    // ================= CONFIGURABLE COMBO ITEMS =================
    comboConfigurableSaving,
    insertComboConfigurable,

    // ================= CATALOG V3 =================
    catalogV3Loading,
    getCatalogItemsV3,

    // ================= OPTION GROUP ITEMS =================
    optionGroupItems,
    optionGroupItemsLoading,
    optionGroupSaving,
    insertComboOptionGroupDetails,
    getOptionGroupItems,
  };
}
